// ---------------------------------------------------------------------------
// Tenant del SUBDOMINIO (multi-tenant).
//
// El hostname decide el tenant: tenant1.soedia.com → 'tenant1' (slug_name en la
// tabla tenants). La cabecera X-Tenant-ID viaja en TODAS las peticiones (ver
// getTenantHeaders en lib/api.js), el login la resuelve y el JWT vuelve con el
// tenant FIJADO (userId + tenantId); a partir de ahí el gatekeeper del backend
// cruza cabecera ↔ JWT en cada petición y las consultas filtran por el tenant
// del JWT — la cabecera NUNCA autoriza nada por sí sola.
//
// SIN window en el módulo (acceso PEREZOSO): las pruebas de Node importan este
// archivo a través de api.js y allí `window` no existe.
// ---------------------------------------------------------------------------

/** Etiquetas de host sin tenant: `www.`, localhost y las IPs (no hay subdominio). */
const RESERVED = new Set(['www', 'localhost', '']);

/** SOLO `npm run dev`: ?as=admin entra al backoffice sin el subdominio admin.localhost (IP/localhost). */
export const devAsAdmin = () =>
  !!import.meta.env?.DEV && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('as') === 'admin';

/**
 * Extrae el slug del tenant del hostname (la etiqueta del subdominio).
 *
 * @param {string} [hostname]  p.ej. window.location.hostname; si se omite, usa window.
 * @returns {string|null} Slug ('tenant1') o null (localhost / IP / sin subdominio).
 */
export function parseTenantId(hostname) {
  if (hostname === undefined && devAsAdmin()) return 'admin';
  hostname ??= typeof window !== 'undefined' ? window.location.hostname : '';
  const host = String(hostname || '').toLowerCase().trim().replace(/^www\./, '');
  // localhost y las IPs no tienen subdominio: no hay tenant que mandar.
  if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;
  const [label] = host.split('.');
  return label && !RESERVED.has(label) ? label : null;
}

/** Etiqueta de host que identifica el BACKOFFICE del super admin. */
const ADMIN_LABEL = 'admin';

/**
 * Decide qué aplicación montar según el subdominio (ver App.jsx).
 *
 * @param {string} [hostname]  p.ej. window.location.hostname; si se omite, usa window.
 * @returns {'admin'|'tenant'}  'admin' en admin.soedia.com / admin.localhost;
 *                              'tenant' en TODO lo demás (incluido localhost y las
 *                              IPs, que caen en la app del negocio por defecto).
 */
export function parseApp(hostname) {
  const label = parseTenantId(hostname);
  return label === ADMIN_LABEL ? 'admin' : 'tenant';
}

let cached; // undefined = todavía no se calculó en esta carga de página

/** Slug del tenant de ESTA página (se calcula UNA sola vez por carga). */
export function getTenantId() {
  if (cached !== undefined) return cached;
  cached = typeof window !== 'undefined' ? parseTenantId() : null;
  return cached;
}

/** Cabeceras de tenant para TODA petición saliente (vacías si no hay subdominio). */
export function getTenantHeaders() {
  const tenantId = getTenantId();
  return tenantId ? { 'X-Tenant-ID': tenantId } : {};
}
